import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Mic, MicOff, Video, VideoOff, PhoneOff, SwitchCamera, Volume2, VolumeX, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export interface CallPeer {
  id?: string | null;
  name: string;
  avatar?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  peer: CallPeer;
  mode: "voice" | "video";
  /** True when the other side is offline — the call rings as a queued push instead. */
  queued?: boolean;
}

/** Instagram-style voice / video call surface used from messages and profiles. */
const CallDialog = ({ open, onOpenChange, peer, mode, queued = false }: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(mode === "video");
  const [speakerOn, setSpeakerOn] = useState(true);
  const [facing, setFacing] = useState<"user" | "environment">("user");
  const [connected, setConnected] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const start = async (wantVideo: boolean, mode2: "user" | "environment" = facing) => {
    try {
      stop();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: wantVideo ? { facingMode: mode2, width: { ideal: 1280 } } : false,
        audio: true,
      });
      streamRef.current = stream;
      stream.getAudioTracks().forEach((t) => (t.enabled = micOn));
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
    } catch {
      toast.error("Could not access your camera or microphone.");
    }
  };

  useEffect(() => {
    if (!open) { stop(); setConnected(false); setSeconds(0); return; }
    setCamOn(mode === "video");
    void start(mode === "video");
    const t = setTimeout(() => { if (!queued) setConnected(true); }, 1800);
    return () => { clearTimeout(t); stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  useEffect(() => {
    if (!connected) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [connected]);

  const toggleMic = () => {
    const next = !micOn;
    setMicOn(next);
    streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = next));
  };

  const toggleCam = () => {
    const next = !camOn;
    setCamOn(next);
    if (next) void start(true);
    else streamRef.current?.getVideoTracks().forEach((t) => { t.stop(); });
  };

  const flip = () => {
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    if (camOn) void start(true, next);
  };

  const end = () => { stop(); onOpenChange(false); toast.success("Call ended"); };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const initials = peer.name.slice(0, 2).toUpperCase();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) end(); }}>
      <DialogContent className="max-w-md border-border bg-background p-0 overflow-hidden">
        <div className="relative aspect-[9/14] max-h-[78vh] w-full bg-gradient-to-b from-primary/25 via-background to-background">
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className={`absolute inset-0 h-full w-full object-cover ${camOn ? "" : "hidden"} ${facing === "user" ? "scale-x-[-1]" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-background/60" />

          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
            <Badge className="border-0 bg-primary/20 text-primary-foreground">
              {mode === "video" ? "Video call" : "Voice call"}
            </Badge>
            {connected && <span className="rounded-full bg-background/70 px-2 py-0.5 text-xs text-foreground">{mmss}</span>}
          </div>

          <div className="absolute inset-x-0 top-1/4 flex flex-col items-center gap-3 text-center">
            <Avatar className="h-28 w-28 border-4 border-primary/30">
              <AvatarImage src={peer.avatar ?? undefined} alt={peer.name} />
              <AvatarFallback className="bg-primary/20 text-2xl text-foreground">{initials}</AvatarFallback>
            </Avatar>
            <h2 className="text-2xl font-semibold text-foreground">{peer.name}</h2>
            <p className="text-sm text-muted-foreground">
              {queued ? "Unavailable — call queued as a notification" : connected ? "Connected" : "Ringing…"}
            </p>
            {queued && (
              <p className="max-w-[16rem] text-xs text-muted-foreground/80">
                {peer.name} isn't online right now. They'll see a missed-call alert the moment they're back.
              </p>
            )}
          </div>

          <div className="absolute inset-x-0 bottom-0 space-y-4 p-5">
            <div className="flex items-center justify-around rounded-full border border-border bg-card/80 p-2 backdrop-blur">
              <Button variant="ghost" size="icon" aria-label="Toggle microphone" onClick={toggleMic} className="h-12 w-12 rounded-full">
                {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5 text-destructive" />}
              </Button>
              <Button variant="ghost" size="icon" aria-label="Toggle camera" onClick={toggleCam} className="h-12 w-12 rounded-full">
                {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5 text-destructive" />}
              </Button>
              <Button variant="ghost" size="icon" aria-label="Flip camera" onClick={flip} className="h-12 w-12 rounded-full">
                <SwitchCamera className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Toggle speaker" onClick={() => setSpeakerOn((s) => !s)} className="h-12 w-12 rounded-full">
                {speakerOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
              <Button variant="ghost" size="icon" aria-label="In-call message" onClick={() => toast.info("Keep chatting in Messages while you talk.")} className="h-12 w-12 rounded-full">
                <MessageCircle className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex justify-center">
              <Button onClick={end} className="h-14 w-14 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90" aria-label="End call">
                <PhoneOff className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CallDialog;
